import { FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ContactUsForm from './ContactUsForm';
import ContentCms from './ContentCms';
import SliderSearch from './SearchSlider';
import { MainContext } from '../contexts/MainContext';
import { getPage } from '@bepro-travel/fe.shared';
import { Page, PageItem } from '../types';
import Sales from './Sales';
import { cdnUrl } from '../constants';
import MainPageSkeleton from './MainPageSkeleton';

interface MainPageProps {
  tabsDesign?: number;
  pageName?: string;
}

const MainPage: FC<MainPageProps> = (props) => {
  const [slider, setSlider] = useState<PageItem>();
  const [searchBox, setSearchBox] = useState('');
  const [pageContents, setPageContents] = useState<PageItem[]>([]);
  const [banners, setBanners] = useState<PageItem[]>([]);
  const [tabs, setTabs] = useState<PageItem[]>([]);
  const [topDestinations, setTopDestinations] = useState<PageItem[]>([]);
  const { setHeader, setFooter, setSiteConfig } = useContext(MainContext);
  const location = useLocation();
  const [pageCity, setPageCity] = useState<Page>();
  const [sales, setSales] = useState<PageItem[]>([]);
  const [offlinePackages, setOfflinePackages] = useState<PageItem[]>([]);
  const [eventSales, setEventSales] = useState<PageItem[]>([]);
  const [contactUs, setContactUs] = useState<PageItem>();
  const [isLoading, setIsLoading] = useState(true);
  const [backgroundUrl, setBackgroundUrl] = useState<string>();
  const blobPath = '/backend';

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setSlider(undefined);
      setSearchBox('');
      setPageContents([]);
      setBanners([]);
      setTabs([]);
      setTopDestinations([]);
      setPageCity(undefined);
      setSales([]);
      setOfflinePackages([]);
      setEventSales([]);
      setBackgroundUrl(undefined);

      const page = await getPage(
        props.pageName !== undefined
          ? props.pageName
          : location.pathname.replace('/', '') + location.search,
        false
      );

      if (page?.pageItems?.length) {
        const isBackgroundActive = page.pageItems.some(
          (item) => item.cmsAttribute === 'cmsBackground'
        );

        const newBackgroundUrl =
          isBackgroundActive && page.page.listOfGalleryId.length
            ? cdnUrl + blobPath + page.page.listOfGalleryId[0].code
            : undefined;
        setBackgroundUrl(newBackgroundUrl);

        const newSlider = page.pageItems.find(
          (item) => item.cmsAttribute === 'cmsSlider' && item.slider
        );

        const isFlight = !!location.pathname.toLowerCase().includes('flight');
        if (isFlight) {
          sessionStorage.setItem('loadingFlight', JSON.stringify(page.loading));
        }

        if (newSlider) {
          if (typeof newSlider.slider?.listOfGalleryId?.[0]?.code === 'string') {
            sessionStorage.setItem('sliderFirstImg', newSlider.slider?.listOfGalleryId[0].code);
          }
          setSlider(newSlider);
        }

        const newSearchBoxItem = page.pageItems.find(
          (item) => item.cmsAttribute === 'cmsSearchBox'
        );
        const newSearchBox = newSearchBoxItem
          ? newSearchBoxItem.searchBox?.matrixConfig?.matrixConfig || 'Multi'
          : '';
        setSearchBox(newSearchBox);

        const newPageContents = page.pageItems.filter(
          (item) => item.cmsAttribute.includes('cmsContent') && item.pageContent
        );
        setPageContents(newPageContents);

        const newBanners = page.pageItems.filter(
          (item) => item.cmsAttribute.includes('cmsBanner') && item.pageBanners?.length
        );
        setBanners(newBanners);

        const newTabs = page.pageItems.filter(
          (item) => item.cmsAttribute.includes('cmsTabs') && item.pageTabs?.length
        );

        setTabs(newTabs);

        const findAllParents = (dest: Page, allDest: Page[]): Page[] => {
          if (
            dest.listOfParamsAttr.length &&
            dest.listOfParamsAttr[0].cmsAttributes === 'cmsParent'
          ) {
            const parent = allDest.find((allDest) => {
              return allDest.id === dest.listOfParamsAttr[0].id;
            });
            if (parent) {
              return [parent, ...findAllParents(parent, allDest)];
            } else {
              return [];
            }
          }
          return [];
        };

        const newTopDestinations = page.pageItems
          .filter((item) => item.topDestinations && item.topDestinations.destionations?.length)
          .map((item) => ({
            ...item,
            topDestinations: {
              ...item.topDestinations,
              destionations: item.topDestinations.destionations.map((dest) => {
                let result = { ...dest };
                result.parents = findAllParents(
                  dest.page,
                  item.topDestinations.allDestinations
                ).reverse();
                return result;
              }),
            },
          }));

        const newSales = page.pageItems.filter(
          (item) => item.cmsAttribute.includes('cmsSales') && item.sales?.tabs?.length
        );

        const newOfflinePackages = page.pageItems.filter(
          (item) =>
            item.cmsAttribute.includes('OfflineSales') &&
            item.offlineSales?.tabs.length &&
            item.offlineSales?.tabs[0].deals?.length
        );

        const newContactUs = page.pageItems.find((item) => item.cmsAttribute === 'cmsContactUs');

        const newEventSales = page.pageItems.filter(
          (item) => item.cmsAttribute.includes('EventSales') && item.eventSales?.tabs.length
        );

        setEventSales(newEventSales);
        setContactUs(newContactUs);
        setOfflinePackages(newOfflinePackages);
        setSales(newSales);
        setTopDestinations(newTopDestinations);
        setPageCity(page.page);
      }

      if (page?.pageHeader) {
        setHeader(page.pageHeader);
      }
      if (page?.pageFooter) {
        setFooter(page.pageFooter);
      }
      if (page?.siteConfig) {
        setSiteConfig(page.siteConfig);
      }

      setIsLoading(false);
    })();
  }, [location]); // eslint-disable-line

  const onImageLoadError = ({ currentTarget }: SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    currentTarget.src = require('../assets/img/newbg_flights.jpg');
  };

  return (
    <div className="main-page">
      {isLoading && (
        <div className="main-page-search">
          <MainPageSkeleton />
        </div>
      )}
      {!!backgroundUrl && (
        <div className="main-page-bg" style={{ backgroundImage: `url(${backgroundUrl})` }}></div>
      )}
      <SliderSearch searchBox={searchBox} slider={slider} page={pageCity} />
      {!!contactUs && <ContactUsForm order={contactUs.orderDisplay} />}

      <ContentCms contents={pageContents} />
      <Sales sales={sales} onImageLoadError={onImageLoadError} />
    </div>
  );
};

export default MainPage;
